/**
 * AppBoot.js
 *
 * Purpose (GH #179):
 * - Render's free tier sleeps the backend after ~15 min idle; the first
 *   request after sleep takes 30-60s to wake it, during which the app would
 *   otherwise just hang on a blank screen while axios calls silently retry.
 * - AppBoot gates the real <App/> behind a bounded health-check poll against
 *   `GET {origin}/health` (NOT `/api/health` — see main.py, `/health` is a
 *   top-level route, not under the `/api` prefix). While the backend is
 *   unreachable it shows a friendly full-screen "waking up" state instead of
 *   a blank/hung page; once `/health` responds OK it renders <App/>.
 *
 * URL resolution: reuses `resolveApiBaseUrl()` (the same precedence used by
 * the rest of the app: explicit REACT_APP_API_BASE_URL > Codespaces origin
 * derivation > same-origin fallback), then strips the trailing `/api` since
 * `/health` lives at the API origin's root, not under `/api`.
 */

import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import App from '../App';
import { resolveApiBaseUrl } from '../services/resolveApiBaseUrl';

// How often to retry the health check while waking up.
export const POLL_INTERVAL_MS = 4000;
// Total time to keep retrying before switching to the "taking longer than
// usual" message (still keeps polling — it just stops pretending this is
// normal).
export const TIMEOUT_MS = 90000;
// Bound each individual health-check attempt so a hung cold instance doesn't
// block the poll loop indefinitely (a sleeping Render instance may just hang
// rather than connection-refuse).
const REQUEST_TIMEOUT_MS = 8000;

// `/health` is a root-level route (see src/web/backend/main.py), not under
// `/api` — resolveApiBaseUrl() always returns a URL ending in `/api`.
function healthUrl() {
  const apiBase = resolveApiBaseUrl();
  const origin = apiBase.replace(/\/api\/?$/, '');
  return `${origin}/health`;
}

async function checkHealth() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(healthUrl(), { signal: controller.signal });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function WakingUp({ timedOut }) {
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 420, alignItems: 'center' }}>
        <CircularProgress />
        <Typography variant="h6">Waking up the service…</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {timedOut
            ? "Still waking up — this is taking longer than usual. It should be ready shortly; feel free to keep waiting."
            : 'This can take up to a minute on first load after a period of inactivity.'}
        </Typography>
      </Stack>
    </Box>
  );
}

// Wraps <App/> behind a bounded health-check poll. Exported as a named
// export (in addition to default) so tests can import it directly.
export function AppBoot() {
  const [ready, setReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const attempt = async () => {
      const ok = await checkHealth();
      if (cancelled) return;
      if (ok) {
        setReady(true);
        clearInterval(intervalId);
        return;
      }
      if (Date.now() - startRef.current >= TIMEOUT_MS) {
        setTimedOut(true);
      }
    };

    attempt();
    intervalId = setInterval(attempt, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  if (!ready) {
    return <WakingUp timedOut={timedOut} />;
  }
  return <App />;
}

export default AppBoot;
